import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, RefreshCw, Download, Printer, Filter, Calendar, FileText, MapPin, User, Hash, CheckCircle, ChevronDown } from 'lucide-react';
import MainService from '../services/mainservice';
import { useToast } from '../contexts/ToastContext';
import * as XLSX from 'xlsx';

const OrderReports = () => {
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]); // For Excel export
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const observerRef = useRef();
  const [filter, setFilter] = useState({
    dateFrom: new Date().toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    IsPickupDate: false,
    IsCreatedDate: false,
    statusIds: [],
    areaIds: [],
    voucherNo: '',
    billNo: '',
    keyword: ''
  });
  const [statusOptions, setStatusOptions] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    loadStatusOptions();
    loadAreaOptions();
    loadOrders();
  }, []);

  const loadStatusOptions = async () => {
    try {
      const response = await MainService.statusInGrid({});
      console.log('Status options response:', response);
      // Handle different response structures
      if (Array.isArray(response)) {
        setStatusOptions(response);
      } else if (response && Array.isArray(response.data)) {
        setStatusOptions(response.data);
      } else if (response && Array.isArray(response.Data)) {
        setStatusOptions(response.Data);
      } else {
        console.warn('Unexpected status options response structure:', response);
        setStatusOptions([]);
      }
    } catch (error) {
      console.error('Error loading status options:', error);
      setStatusOptions([]);
    }
  };

  const loadAreaOptions = async () => {
    try {
      const response = await MainService.AreasInGrid({});
      // Handle different response structures
      if (Array.isArray(response)) {
        setAreaOptions(response);
      } else if (response && Array.isArray(response.data)) {
        setAreaOptions(response.data);
      } else if (response && Array.isArray(response.Data)) {
        setAreaOptions(response.Data);
      } else {
        console.warn('Unexpected area options response structure:', response);
        setAreaOptions([]);
      }
    } catch (error) {
      console.error('Error loading area options:', error);
      setAreaOptions([]);
    }
  };

  const loadOrders = useCallback(async (reset = true) => {
    if (reset) {
      setLoading(true);
      setSkip(0);
      setOrders([]);
      setAllOrders([]);
      setHasMore(true);
    } else {
      setIsLoadingMore(true);
    }

    const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
    if (!tokenData) {
      showError('Please login first');
      return;
    }

    const userData = JSON.parse(tokenData);

    try {
      const filterData = {
        ...filter,
        StatusIds: selectedStatus,
        AreaIds: selectedAreas,
        FromMonth: new Date(filter.dateFrom).getMonth() + 1,
        FromDay: new Date(filter.dateFrom).getDate(),
        FromYear: new Date(filter.dateFrom).getFullYear(),
        ToMonth: new Date(filter.dateTo).getMonth() + 1,
        ToDay: new Date(filter.dateTo).getDate(),
        ToYear: new Date(filter.dateTo).getFullYear(),
        ClientId: userData.ClientId,
        Skip: reset ? 0 : skip,
        Take: 20
      };

      const response = await MainService.OrderReportInPortal(filterData);
      // Handle response structure with Orders and TotalCount
      let ordersData = [];
      let totalCount = 0;
      
      if (response && response.Orders && Array.isArray(response.Orders)) {
        ordersData = response.Orders;
        totalCount = response.TotalCount || 0;
      } else if (Array.isArray(response)) {
        ordersData = response;
        totalCount = response.length;
      } else if (response && Array.isArray(response.data)) {
        ordersData = response.data;
        totalCount = response.data.length;
      } else if (response && Array.isArray(response.Data)) {
        ordersData = response.Data;
        totalCount = response.Data.length;
      } else {
        console.warn('Unexpected orders response structure:', response);
        ordersData = [];
        totalCount = 0;
      }

      if (reset) {
        setOrders(ordersData);
        setAllOrders(ordersData);
        setTotalCount(totalCount);
        showSuccess(`Loaded ${ordersData.length} orders successfully`);
        console.log('Reset load - Orders:', ordersData.length, 'Total:', totalCount);
      } else {
        setOrders(prev => {
          const newOrders = [...prev, ...ordersData];
          console.log('Appending orders - Previous:', prev.length, 'New:', ordersData.length, 'Total:', newOrders.length);
          return newOrders;
        });
        setAllOrders(prev => {
          const newAllOrders = [...prev, ...ordersData];
          console.log('Appending to allOrders - Previous:', prev.length, 'New:', ordersData.length, 'Total:', newAllOrders.length);
          return newAllOrders;
        });
      }

      setSkip(prev => prev + 20);
      // Check if we have more orders to load
      const currentLoadedCount = reset ? ordersData.length : (allOrders.length + ordersData.length);
      // Continue loading if we haven't reached the total count
      const hasMoreData = currentLoadedCount < totalCount;
      setHasMore(hasMoreData);
      
      console.log('Pagination Debug:', {
        ordersDataLength: ordersData.length,
        currentLoadedCount,
        totalCount,
        hasMoreData,
        skip: skip + 20,
        remainingOrders: totalCount - currentLoadedCount
      });
    } catch (error) {
      console.error('Error loading orders:', error);
      showError('Failed to load order reports');
      if (reset) {
        setOrders([]);
        setAllOrders([]);
      }
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [filter, selectedStatus, selectedAreas, skip, showError, showSuccess]);

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilter = () => {
    loadOrders(true);
  };

  // Infinite scroll observer
  const lastOrderElementRef = useCallback(node => {
    if (isLoadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      console.log('Observer triggered:', {
        isIntersecting: entries[0].isIntersecting,
        hasMore,
        isLoadingMore,
        currentOrdersLength: orders.length
      });
      if (entries[0].isIntersecting && hasMore) {
        console.log('Loading more orders...');
        loadOrders(false);
      }
    });
    if (node) observerRef.current.observe(node);
  }, [isLoadingMore, hasMore, loadOrders, orders.length]);

  const handleRefresh = () => {
    setFilter({
      dateFrom: new Date().toISOString().split('T')[0],
      dateTo: new Date().toISOString().split('T')[0],
      IsPickupDate: false,
      IsCreatedDate: false,
      statusIds: [],
      areaIds: [],
      voucherNo: '',
      billNo: '',
      keyword: '',
      batchNo: ''
    });
    setSelectedStatus([]);
    setSelectedAreas([]);
    loadOrders(true);
  };

  const handlePrint = async () => {
    try {
      setIsPrinting(true);
      
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (!tokenData) {
        showError('Please login first');
        return;
      }

      const userData = JSON.parse(tokenData);
      
      const filterData = {
        ...filter,
        statusIds: selectedStatus,
        areaIds: selectedAreas,
        fromMonth: new Date(filter.dateFrom).getMonth() + 1,
        fromDay: new Date(filter.dateFrom).getDate(),
        fromYear: new Date(filter.dateFrom).getFullYear(),
        toMonth: new Date(filter.dateTo).getMonth() + 1,
        toDay: new Date(filter.dateTo).getDate(),
        toYear: new Date(filter.dateTo).getFullYear(),
        ClientId: userData.ClientId
      };

      const response = await MainService.PrintClientOrders(filterData);
      if (response) {
        window.open(`${process.env.REACT_APP_BASE_PATH || ''}/ReportFiles/${response}`, '_blank');
        showSuccess('Print request sent successfully');
      } else {
        showError('Print Error');
      }
    } catch (error) {
      console.error('Print error:', error);
      showError('Print Error');
    } finally {
      setIsPrinting(false);
    }
  };


  const handleExportToExcel = () => {
    if (!Array.isArray(allOrders) || allOrders.length === 0) {
      showError('No data to export');
      return;
    }

    try {
      // Prepare data for Excel export
      const excelData = allOrders.map((order, index) => ({
        'S.No': index + 1,
        'Voucher No': order.VoucherNo || 'N/A',
        'Client Bill': order.ClientBillNo || 'N/A',
        'Batch No': order.BatchNo || 'N/A',
        'Quantity': order.Quantity || 'N/A',
        'Delivery Date': formatDate(order.DeliveryDate),
        'Product Price': order.ProductPrice ? `AED ${order.ProductPrice}` : 'N/A',
        'Delivery Charge': order.DeliveryCharge ? `AED ${order.DeliveryCharge}` : 'N/A',
        'TAX': order.TAX ? `AED ${order.TAX}` : 'N/A',
        'Customer Amount': order.CustomerAmount ? `AED ${order.CustomerAmount}` : 'N/A',
        'Client Amount': order.ClientAmount ? `AED ${order.ClientAmount}` : 'N/A',
        'Client Emirate': order.ClientAddress?.Emirate?.Name || 'N/A',
        'Client Location': order.ClientAddress?.Location?.Name || 'N/A',
        'Client Address': order.ClientAddress?.Address || 'N/A',
        'Customer': order.Customer?.Name || 'N/A',
        'Customer Mobile': order.Customer?.MobileNo1 || 'N/A',
        'Customer Emirate': order.CustomerAddress?.Emirate?.Name || 'N/A',
        'Customer Location': order.CustomerAddress?.Location?.Name || 'N/A',
        'Status': order.Status?.Name || 'N/A'
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Order Reports');

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Order_Reports_${currentDate}.xlsx`;

      // Save the file
      XLSX.writeFile(wb, filename);
      showSuccess('Excel file exported successfully!');
    } catch (error) {
      console.error('Excel export error:', error);
      showError('Failed to export Excel file');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  return (
    <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Reports</h1>
          <p className="text-gray-600">Generate and view detailed order reports with advanced filtering</p>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Advanced Filters</h2>
              <p className="text-sm text-gray-600 mt-1">Refine your search with multiple criteria</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleFilter}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Filter className="w-5 h-5" />
                <span>Apply Filters</span>
              </button>
              <button
                onClick={handleRefresh}
                className="bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-md hover:shadow-lg"
              >
                <RefreshCw className="w-5 h-5" />
                <span>Reset</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-primary-600" />
                From Date
              </label>
              <input
                type="date"
                value={filter.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-primary-600" />
                To Date
              </label>
              <input
                type="date"
                value={filter.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <CheckCircle className="w-4 h-4 mr-2 text-primary-600" />
                Date Type
              </label>
              <div className="space-y-3 p-4 bg-gray-50 rounded-xl">
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={filter.IsPickupDate}
                    onChange={(e) => handleFilterChange('IsPickupDate', e.target.checked)}
                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700 group-hover:text-primary-700">Pickup Date</span>
                </label>
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={filter.IsCreatedDate}
                    onChange={(e) => handleFilterChange('IsCreatedDate', e.target.checked)}
                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700 group-hover:text-primary-700">Created Date</span>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <Hash className="w-4 h-4 mr-2 text-primary-600" />
                Voucher No
              </label>
              <input
                type="text"
                placeholder="Enter voucher number"
                value={filter.voucherNo}
                onChange={(e) => handleFilterChange('voucherNo', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white placeholder-gray-400"
              />
            </div>
          </div>

          {/* Show More/Hide Button */}
          <div className="flex justify-center mb-6">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 font-semibold py-2 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-md hover:shadow-lg"
            >
              {showAdvancedFilters ? (
                <>
                  <ChevronDown className="w-4 h-4" />
                  <span>Hide Advanced Filters</span>
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 rotate-180" />
                  <span>Show Advanced Filters</span>
                </>
              )}
            </button>
          </div>

          {/* Advanced Filters - Collapsible */}
          {showAdvancedFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                  <FileText className="w-4 h-4 mr-2 text-primary-600" />
                  Status
                </label>
                <div className="relative">
                  <select
                    multiple
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(Array.from(e.target.selectedOptions, option => option.value))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                    size="4"
                  >
                    {Array.isArray(statusOptions) && statusOptions.map((status) => (
                      <option key={status.Id || status.id} value={status.Id || status.id}>
                        {status.Name || status.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
                <p className="text-xs text-gray-500">Hold Ctrl/Cmd to select multiple statuses</p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-primary-600" />
                  Delivery Areas
                </label>
                <div className="relative">
                  <select
                    multiple
                    value={selectedAreas}
                    onChange={(e) => setSelectedAreas(Array.from(e.target.selectedOptions, option => option.value))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                    size="4"
                  >
                    {Array.isArray(areaOptions) && areaOptions.map((area) => (
                      <option key={area.Id || area.id} value={area.Id || area.id}>
                        {area.Name || area.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
                <p className="text-xs text-gray-500">Hold Ctrl/Cmd to select multiple areas</p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                  <FileText className="w-4 h-4 mr-2 text-primary-600" />
                  Bill No
                </label>
                <input
                  type="text"
                  placeholder="Enter bill number"
                  value={filter.billNo}
                  onChange={(e) => handleFilterChange('billNo', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white placeholder-gray-400"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                  <Search className="w-4 h-4 mr-2 text-primary-600" />
                  Keyword
                </label>
                <input
                  type="text"
                  placeholder="Search keyword"
                  value={filter.keyword}
                  onChange={(e) => handleFilterChange('keyword', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white placeholder-gray-400"
                />
              </div>
            </div>
          )}
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Order Reports</h2>
              <p className="text-sm text-gray-600 mt-1">Detailed view of all orders with comprehensive information</p>
            </div>
            <div className="flex items-center space-x-4">
                <div className="bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                  <span className="text-sm font-medium text-gray-600">Total Orders:</span>
                  <span className="ml-2 text-lg font-bold text-primary-600">{totalCount}</span>
                  {allOrders.length < totalCount && (
                    <span className="ml-2 text-sm text-gray-500">({allOrders.length} loaded)</span>
                  )}
                </div>
              <button
                onClick={handleExportToExcel}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Download className="w-4 h-4" />
                <span>Export Excel</span>
              </button>
              <button
                onClick={handlePrint}
                disabled={isPrinting}
                className={`bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                  isPrinting ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {isPrinting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Printing...</span>
                  </>
                ) : (
                  <>
                    <Printer className="w-4 h-4" />
                    <span>Print</span>
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto overflow-y-visible rounded-xl border border-gray-200">
            <table className="w-full" style={{ minWidth: '1200px' }}>
              <thead>
                <tr className="bg-gradient-to-r from-gray-100 to-gray-200">
                  <th className="px-4 py-4 text-center text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">#</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Voucher No</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Client Bill</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Batch No</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Quantity</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Delivery Date</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Product Price</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Delivery Charge</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">TAX</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Customer Amount</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Client Amount</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Client Emirate</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Client Location</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Client Address</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Customer</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Customer Mob</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Customer Emirate</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Customer Location</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="19" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                          <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
                        </div>
                        <p className="text-lg font-medium text-gray-700">Loading orders...</p>
                        <p className="text-sm text-gray-500 mt-1">Please wait while we fetch your data</p>
                      </div>
                    </td>
                  </tr>
                ) : !Array.isArray(orders) || orders.length === 0 ? (
                  <tr>
                    <td colSpan="19" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <FileText className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-lg font-medium text-gray-700">No orders found</p>
                        <p className="text-sm text-gray-500 mt-1">Try adjusting your filters to see more results</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  orders.map((order, index) => (
                    <tr 
                      key={index} 
                      className="hover:bg-gray-50 transition-all duration-200 group border-b border-gray-100"
                      ref={index === orders.length - 1 ? lastOrderElementRef : null}
                    >
                      <td className="px-4 py-4 text-center text-sm text-gray-500 font-medium border-r border-gray-100">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 text-sm font-bold text-gray-900 group-hover:text-primary-700 border-r border-gray-100">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                          <span>{order.VoucherNo || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">{order.ClientBillNo || 'N/A'}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">{order.BatchNo || 'N/A'}</td>
                      <td className="px-4 py-4 text-sm text-center border-r border-gray-100">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 text-xs font-bold rounded-full">
                          {order.Quantity || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">{formatDate(order.DeliveryDate)}</td>
                      <td className="px-4 py-4 text-sm font-bold text-gray-900 border-r border-gray-100">
                        {order.ProductPrice ? `AED ${order.ProductPrice}` : 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">
                        {order.DeliveryCharge ? `AED ${order.DeliveryCharge}` : 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">
                        {order.TAX ? `AED ${order.TAX}` : 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm font-bold text-green-600 border-r border-gray-100">
                        {order.CustomerAmount ? `AED ${order.CustomerAmount}` : 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm font-bold text-blue-600 border-r border-gray-100">
                        {order.ClientAmount ? `AED ${order.ClientAmount}` : 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">{order.ClientAddress?.Emirate?.Name || 'N/A'}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">{order.ClientAddress?.Location?.Name || 'N/A'}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 max-w-xs truncate border-r border-gray-100">{order.ClientAddress?.Address || 'N/A'}</td>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900 border-r border-gray-100">{order.Customer?.Name || 'N/A'}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">{order.Customer?.MobileNo1 || 'N/A'}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">{order.CustomerAddress?.Emirate?.Name || 'N/A'}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">{order.CustomerAddress?.Location?.Name || 'N/A'}</td>
                      <td className="px-4 py-4 text-sm border-r border-gray-100">
                        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                          {order.Status?.Name || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Loading More Indicator */}
          {isLoadingMore && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-2"></div>
              <span className="text-gray-600">Loading more orders...</span>
            </div>
          )}
          
          {/* Load More Button (Fallback) */}
          {hasMore && orders.length > 0 && !isLoadingMore && (
            <div className="text-center py-4">
              <button
                onClick={() => loadOrders(false)}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200"
              >
                Load More Orders ({totalCount - allOrders.length} remaining)
              </button>
            </div>
          )}

          {/* End of Results */}
          {!hasMore && orders.length > 0 && !isLoadingMore && (
            <div className="text-center py-4 text-gray-500 text-sm">
              No more orders to load
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default OrderReports;
